"use client";

import React, { useState } from "react";
import { Activity, ArrowUpRight, ChartBar as BarChart3, Bot, CircleCheck as CheckCircle2, ChevronRight, Globe, Lock, PhoneCall, RefreshCw, Search, ShieldCheck, Sparkles, TrendingUp, Users } from 'lucide-react';
import { motion, AnimatePresence } from "framer-motion";
import { Area, AreaChart, CartesianGrid, Cell, Pie, PieChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { ChartContainer, ChartTooltip, ChartTooltipContent } from "@/components/ui/chart";
import { Separator } from "@/components/ui/separator";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
const dashboardTabs = [{
  id: "traffic",
  label: "Website Traffic",
  iconName: "Globe",
  description: "Organic AI search referral traffic versus traditional search engines."
}, {
  id: "ai-visibility",
  label: "AI Visibility",
  iconName: "Bot",
  description: "Share of voice across ChatGPT, Claude, Perplexity, and Google Gemini."
}, {
  id: "lead-sources",
  label: "Lead Sources",
  iconName: "Users",
  description: "Revenue attribution broken down by AI recommendation vs traditional channels."
}, {
  id: "conversion",
  label: "Conversion Rates",
  iconName: "TrendingUp",
  description: "Full-funnel breakdown from AI mention to confirmed booking."
}, {
  id: "calls",
  label: "Calls & Appointments",
  iconName: "PhoneCall",
  description: "Real-time call logs and automated booking confirmation stream."
}, {
  id: "campaigns",
  label: "Campaign Performance",
  iconName: "Activity",
  description: "Active structured schema, knowledge graph sync, and indexing health."
}];
const trafficData = [{
  month: "Jan",
  traditional: 1200,
  aiSearch: 450
}, {
  month: "Feb",
  traditional: 1350,
  aiSearch: 680
}, {
  month: "Mar",
  traditional: 1400,
  aiSearch: 1120
}, {
  month: "Apr",
  traditional: 1520,
  aiSearch: 1850
}, {
  month: "May",
  traditional: 1600,
  aiSearch: 2640
}, {
  month: "Jun",
  traditional: 1680,
  aiSearch: 3420
}];
const aiVisibilityData = [{
  platform: "ChatGPT 4o",
  score: 94,
  change: "+18%",
  citations: "1,240"
}, {
  platform: "Perplexity AI",
  score: 88,
  change: "+24%",
  citations: "890"
}, {
  platform: "Claude 3.5 Sonnet",
  score: 91,
  change: "+15%",
  citations: "760"
}, {
  platform: "Google Gemini",
  score: 82,
  change: "+31%",
  citations: "1,100"
}, {
  platform: "Apple Intelligence",
  score: 79,
  change: "+12%",
  citations: "540"
}];
const leadSourcesData = [{
  name: "AI Search (GEO)",
  value: 48,
  color: "hsl(352, 54%, 33%)"
}, {
  name: "Local Map Pack",
  value: 26,
  color: "hsl(16, 19%, 35%)"
}, {
  name: "Organic Web",
  value: 16,
  color: "hsl(17, 4%, 40%)"
}, {
  name: "Direct / Referral",
  value: 10,
  color: "hsl(120, 0%, 30%)"
}];
const conversionFunnelData = [{
  stage: "AI Search Mentions",
  count: "12,450",
  percentage: 100,
  drop: "0%"
}, {
  stage: "Website / Profile Visits",
  count: "4,120",
  percentage: 33,
  drop: "-67%"
}, {
  stage: "Call / Form Initiated",
  count: "890",
  percentage: 21.6,
  drop: "-78%"
}, {
  stage: "Booked Appointments",
  count: "312",
  percentage: 35,
  drop: "-65%"
}];
const callLogs = [{
  id: 1,
  caller: "Residential HVAC Inquiry",
  source: "ChatGPT Recommendation",
  duration: "4m 12s",
  status: "Booked",
  time: "10 mins ago"
}, {
  id: 2,
  caller: "Commercial Plumbing Quote",
  source: "Perplexity Search",
  duration: "2m 45s",
  status: "Inquiry",
  time: "34 mins ago"
}, {
  id: 3,
  caller: "Emergency AC Repair",
  source: "Google Gemini",
  duration: "6m 01s",
  status: "Booked",
  time: "1 hour ago"
}, {
  id: 4,
  caller: "Duct Inspection Lead",
  source: "Claude 3.5 Sonnet",
  duration: "3m 22s",
  status: "Booked",
  time: "2 hours ago"
}];
const campaignData = [{
  id: 1,
  campaign: "Structured Schema Markup",
  status: "Active",
  health: "99%",
  impact: "High",
  trend: "+28%"
}, {
  id: 2,
  campaign: "LLM Knowledge Graph Synced",
  status: "Verified",
  health: "100%",
  impact: "Critical",
  trend: "+42%"
}, {
  id: 3,
  campaign: "Review Authority Injection",
  status: "Active",
  health: "94%",
  impact: "High",
  trend: "+19%"
}, {
  id: 4,
  campaign: "Local Citation Consistency",
  status: "Optimized",
  health: "97%",
  impact: "Medium",
  trend: "+11%"
}];
const summaryCards = [{
  iconName: "BarChart3",
  title: "100% Attribution Transparency",
  description: "Track every lead back to the exact AI engine prompt or search query that recommended your business."
}, {
  iconName: "Sparkles",
  title: "Automated Knowledge Sync",
  description: "Keep your business facts, services, and live scheduling options synchronized across all LLM datasets."
}, {
  iconName: "ShieldCheck",
  title: "Continuous Reputation Guard",
  description: "Monitor sentiment and accuracy in real-time to ensure AI models consistently present your brand as the top local choice."
}];
const trafficChartConfig = {
  aiSearch: {
    label: "AI Search Referrals",
    color: "hsl(352, 54%, 33%)"
  },
  traditional: {
    label: "Traditional Search",
    color: "hsl(17, 4%, 40%)"
  }
};
export default function Dashboard() {
  const [activeTab, setActiveTab] = useState("traffic");
  const [isRefreshing, setIsRefreshing] = useState(false);
  const handleRefresh = () => {
    setIsRefreshing(true);
    setTimeout(() => setIsRefreshing(false), 800);
  };
  const renderTabIcon = (iconName: string) => {
    switch (iconName) {
      case "Globe":
        return <Globe className="size-4" />;
      case "Bot":
        return <Bot className="size-4" />;
      case "Users":
        return <Users className="size-4" />;
      case "TrendingUp":
        return <TrendingUp className="size-4" />;
      case "PhoneCall":
        return <PhoneCall className="size-4" />;
      case "Activity":
        return <Activity className="size-4" />;
      default:
        return <Activity className="size-4" />;
    }
  };
  const renderSummaryIcon = (iconName: string) => {
    switch (iconName) {
      case "BarChart3":
        return <BarChart3 className="size-6 text-[hsl(352_54%_33%)]" />;
      case "Sparkles":
        return <Sparkles className="size-6 text-[hsl(352_54%_33%)]" />;
      case "ShieldCheck":
        return <ShieldCheck className="size-6 text-[hsl(352_54%_33%)]" />;
      default:
        return <Activity className="size-6 text-[hsl(352_54%_33%)]" />;
    }
  };
  return <section id="interactivedashboardhowitworks" data-nav="dark" className="relative bg-background text-foreground py-20 px-4 md:px-8 border-b border-border overflow-hidden">
      <div className="max-w-7xl mx-auto space-y-12">
        {}
        <div className="text-center space-y-4 max-w-3xl mx-auto">
          <div className="font-mono text-xs font-semibold text-[hsl(352_54%_33%)] uppercase tracking-widest flex items-center justify-center gap-2">
            <span className="inline-block size-2 rounded-full bg-[hsl(352_54%_33%)] animate-pulse" />
            {"[ SYSTEM ARCHITECTURE & ANALYTICS ]"}
          </div>
          <h2 className="font-default text-3xl md:text-5xl font-bold tracking-tight text-foreground">
            How ClearLine Engine Drives AI Visibility
          </h2>
          <p className="text-muted-foreground text-base md:text-lg">
            Explore our live intelligence panel. See how we clean your web presence, train AI models to pick your business, and measure direct appointment conversions.
          </p>
        </div>

        {}
        <Card className="bg-card text-card-foreground border border-border shadow-2xl rounded-none overflow-hidden py-0">
          {}
          <div className="bg-muted/60 border-b border-border px-4 py-3 flex items-center justify-between gap-4">
            <div className="flex items-center gap-2">
              <span className="size-3 rounded-full bg-[hsl(352_54%_33%)]/80 inline-block" />
              <span className="size-3 rounded-full bg-secondary inline-block" />
              <span className="size-3 rounded-full bg-muted-foreground/40 inline-block" />
            </div>

            <div className="flex-1 max-w-xl bg-background/80 border border-border rounded-none px-3 py-1 flex items-center gap-2 text-xs text-muted-foreground font-mono">
              <Lock className="size-3 text-[hsl(352_54%_33%)]" />
              <span className="text-foreground font-medium">https://</span>
              <span className="truncate">app.clearlinegeo.com/analytics/live-performance</span>
            </div>

            <div className="flex items-center gap-3">
              <Badge variant="outline" className="border-[hsl(352_54%_33%)]/40 text-[hsl(352_54%_33%)] bg-[hsl(352_54%_33%)]/10 gap-1 font-mono text-[10px]">
                <span className="size-1.5 rounded-full bg-[hsl(352_54%_33%)] animate-ping" />
                LIVE SYNC
              </Badge>
              <Button variant="ghost" size="icon" onClick={handleRefresh} className="h-7 w-7 text-muted-foreground hover:text-foreground" aria-label="Refresh data">
                <RefreshCw className={`size-3.5 ${isRefreshing ? "animate-spin" : ""}`} />
              </Button>
            </div>
          </div>

          {}
          <div className="grid grid-cols-1 lg:grid-cols-12 min-h-[560px]">
            {}
            <div className="lg:col-span-4 border-b lg:border-b-0 lg:border-r border-border bg-muted/20 p-2 md:p-4 space-y-1">
              <div className="px-3 py-2 text-xs font-mono font-semibold uppercase text-muted-foreground tracking-wider">
                Analytics Views
              </div>
              <div className="space-y-1">
                {dashboardTabs.map((tab, i) => {
                const isActive = activeTab === tab.id;
                return <Button key={tab.id} data-index={i} variant="ghost" onClick={() => setActiveTab(tab.id)} className={`w-full justify-start text-left h-auto py-3 px-3.5 rounded-none border-l-2 transition-all flex items-center justify-between group ${isActive ? "bg-[hsl(352_54%_33%)] text-white border-[hsl(352_54%_33%)] font-semibold shadow-xs" : "border-transparent text-muted-foreground hover:bg-muted/50 hover:text-foreground"}`}>
                      <div className="flex items-center gap-3 truncate">
                        {renderTabIcon(tab.iconName)}
                        <span className="text-sm truncate">{tab.label}</span>
                      </div>
                      <ChevronRight className={`size-4 shrink-0 transition-transform ${isActive ? "translate-x-1 text-white" : "opacity-0 group-hover:opacity-100"}`} />
                    </Button>;
              })}
              </div>

              <div className="pt-4 px-3">
                <div className="p-3 bg-card border border-border space-y-2">
                  <div className="text-xs font-mono font-medium text-foreground flex items-center gap-1.5">
                    <CheckCircle2 className="size-3.5 text-[hsl(352_54%_33%)]" />
                    System Status: 100% Operational
                  </div>
                  <p className="text-[11px] text-muted-foreground leading-tight">
                    Indexing nodes updated across ChatGPT, Perplexity & Google Search.
                  </p>
                </div>
              </div>
            </div>

            {}
            <div className="lg:col-span-8 p-4 md:p-6 bg-card flex flex-col justify-between">
              <AnimatePresence mode="wait">
                <motion.div key={activeTab} initial={{
                opacity: 0,
                y: 10
              }} animate={{
                opacity: 1,
                y: 0
              }} exit={{
                opacity: 0,
                y: -10
              }} transition={{
                duration: 0.25
              }} className="space-y-6 flex-1 flex flex-col justify-between">
                  {}
                  <div>
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 border-b border-border pb-4">
                      <div>
                        <h3 className="text-xl font-bold font-default text-foreground">
                          {dashboardTabs.find(t => t.id === activeTab)?.label}
                        </h3>
                        <p className="text-xs text-muted-foreground mt-0.5">
                          {dashboardTabs.find(t => t.id === activeTab)?.description}
                        </p>
                      </div>
                      <Badge variant="secondary" className="w-fit font-mono text-[11px]">
                        Updated 2m ago
                      </Badge>
                    </div>
                  </div>

                  {}
                  {activeTab === "traffic" && <div className="space-y-6">
                      <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                        <div className="p-3 bg-muted/40 border border-border">
                          <div className="text-xs text-muted-foreground font-mono">Total AI Traffic</div>
                          <div className="text-2xl font-bold text-foreground mt-1">3,420</div>
                          <span className="text-[11px] text-[hsl(352_54%_33%)] font-medium">+184% MoM</span>
                        </div>
                        <div className="p-3 bg-muted/40 border border-border">
                          <div className="text-xs text-muted-foreground font-mono">Trad. Search</div>
                          <div className="text-2xl font-bold text-foreground mt-1">1,680</div>
                          <span className="text-[11px] text-muted-foreground font-medium">+5% MoM</span>
                        </div>
                        <div className="p-3 bg-muted/40 border border-border col-span-2 sm:col-span-1">
                          <div className="text-xs text-muted-foreground font-mono">AI Conversion Rate</div>
                          <div className="text-2xl font-bold text-foreground mt-1">9.1%</div>
                          <span className="text-[11px] text-[hsl(352_54%_33%)] font-medium">3.2x vs Google</span>
                        </div>
                      </div>

                      <div className="h-64 w-full pt-2">
                        <ChartContainer config={trafficChartConfig} className="h-full w-full aspect-auto">
                          <ResponsiveContainer width="100%" height="100%">
                            <AreaChart data={trafficData} margin={{
                          top: 10,
                          right: 10,
                          left: -20,
                          bottom: 0
                        }}>
                              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="hsl(17 2% 20%)" />
                              <XAxis dataKey="month" stroke="hsl(17 4% 60%)" fontSize={11} tickLine={false} />
                              <YAxis stroke="hsl(17 4% 60%)" fontSize={11} tickLine={false} />
                              <ChartTooltip content={<ChartTooltipContent />} />
                              <Area type="monotone" dataKey="aiSearch" name="AI Search Referrals" stroke="hsl(352, 54%, 33%)" strokeWidth={2} fill="hsl(352, 54%, 33%)" fillOpacity={0.25} />
                              <Area type="monotone" dataKey="traditional" name="Traditional Search" stroke="hsl(17, 4%, 40%)" strokeWidth={1.5} fill="hsl(17, 4%, 40%)" fillOpacity={0.1} />
                            </AreaChart>
                          </ResponsiveContainer>
                        </ChartContainer>
                      </div>
                    </div>}

                  {}
                  {activeTab === "ai-visibility" && <div className="space-y-4">
                      <div className="p-4 bg-muted/30 border border-border flex items-center justify-between">
                        <div>
                          <div className="text-xs font-mono text-muted-foreground">Overall GEO Score</div>
                          <div className="text-3xl font-extrabold text-foreground">91.4 <span className="text-sm font-normal text-muted-foreground">/ 100</span></div>
                        </div>
                        <Badge className="bg-[hsl(352_54%_33%)] text-white font-mono">TOP 1% IN REGION</Badge>
                      </div>

                      <div className="space-y-3">
                        {aiVisibilityData.map((item, i) => <div key={item.platform} data-index={i} className="space-y-1.5">
                            <div className="flex items-center justify-between text-xs font-mono">
                              <span className="font-semibold text-foreground flex items-center gap-2">
                                <Bot className="size-3.5 text-[hsl(352_54%_33%)]" />
                                {item.platform}
                              </span>
                              <div className="flex items-center gap-3">
                                <span className="text-muted-foreground">{item.citations} citations</span>
                                <span className="text-[hsl(352_54%_33%)] font-bold">{item.score}/100</span>
                              </div>
                            </div>
                            <div className="h-2 w-full bg-muted overflow-hidden">
                              <motion.div initial={{
                          width: 0
                        }} animate={{
                          width: `${item.score}%`
                        }} transition={{
                          duration: 0.6,
                          delay: i * 0.08
                        }} className="h-full bg-[hsl(352_54%_33%)]" />
                            </div>
                          </div>)}
                      </div>
                    </div>}

                  {}
                  {activeTab === "lead-sources" && <div className="grid grid-cols-1 md:grid-cols-2 gap-6 items-center">
                      <div className="h-60 w-full flex items-center justify-center">
                        <ResponsiveContainer width="100%" height="100%">
                          <PieChart>
                            <Pie data={leadSourcesData} cx="50%" cy="50%" innerRadius={55} outerRadius={85} paddingAngle={4} dataKey="value">
                              {leadSourcesData.map((entry, index) => <Cell key={`cell-${index}`} fill={entry.color} />)}
                            </Pie>
                            <Tooltip formatter={value => [`${value}% Share`, "Contribution"]} contentStyle={{
                          backgroundColor: "hsl(17 6% 10%)",
                          borderColor: "hsl(17 2% 25%)",
                          color: "#fff"
                        }} />
                          </PieChart>
                        </ResponsiveContainer>
                      </div>

                      <div className="space-y-3">
                        <div className="text-xs font-mono uppercase text-muted-foreground tracking-wider">Source Breakdown</div>
                        {leadSourcesData.map((source, i) => <div key={source.name} data-index={i} className="flex items-center justify-between p-2 bg-muted/30 border border-border text-xs">
                            <div className="flex items-center gap-2">
                              <span className="size-2.5 rounded-none" style={{
                          backgroundColor: source.color
                        }} />
                              <span className="font-medium text-foreground">{source.name}</span>
                            </div>
                            <span className="font-mono font-bold text-foreground">{source.value}%</span>
                          </div>)}
                      </div>
                    </div>}

                  {}
                  {activeTab === "conversion" && <div className="space-y-4">
                      <div className="text-xs text-muted-foreground font-mono">
                        Conversion Funnel (Last 30 Days)
                      </div>
                      <div className="space-y-2">
                        {conversionFunnelData.map((stage, i) => <div key={stage.stage} data-index={i} className="p-3 bg-muted/40 border border-border space-y-1">
                            <div className="flex justify-between text-xs font-mono">
                              <span className="font-medium text-foreground">{stage.stage}</span>
                              <span className="text-[hsl(352_54%_33%)] font-bold">{stage.count}</span>
                            </div>
                            <div className="h-2.5 w-full bg-muted overflow-hidden">
                              <motion.div initial={{
                          width: 0
                        }} animate={{
                          width: `${stage.percentage}%`
                        }} transition={{
                          duration: 0.5,
                          delay: i * 0.1
                        }} className="h-full bg-[hsl(352_54%_33%)]" />
                            </div>
                          </div>)}
                      </div>
                    </div>}

                  {}
                  {activeTab === "calls" && <div className="space-y-4">
                      <div className="grid grid-cols-3 gap-3 text-center">
                        <div className="p-2.5 bg-muted/30 border border-border">
                          <div className="text-[11px] font-mono text-muted-foreground">Calls This Month</div>
                          <div className="text-xl font-bold text-foreground">142</div>
                        </div>
                        <div className="p-2.5 bg-muted/30 border border-border">
                          <div className="text-[11px] font-mono text-muted-foreground">Booking Rate</div>
                          <div className="text-xl font-bold text-[hsl(352_54%_33%)]">78%</div>
                        </div>
                        <div className="p-2.5 bg-muted/30 border border-border">
                          <div className="text-[11px] font-mono text-muted-foreground">Avg Duration</div>
                          <div className="text-xl font-bold text-foreground">3.8m</div>
                        </div>
                      </div>

                      <div className="border border-border overflow-x-auto">
                        <Table>
                          <TableHeader className="bg-muted/50">
                            <TableRow>
                              <TableHead className="text-xs font-mono">Lead / Query</TableHead>
                              <TableHead className="text-xs font-mono">AI Source</TableHead>
                              <TableHead className="text-xs font-mono">Status</TableHead>
                              <TableHead className="text-xs font-mono text-right">Time</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {callLogs.map((log, i) => <TableRow key={log.id} data-index={i} className="text-xs">
                                <TableCell className="font-medium text-foreground">{log.caller}</TableCell>
                                <TableCell className="text-muted-foreground">{log.source}</TableCell>
                                <TableCell>
                                  <Badge variant={log.status === "Booked" ? "default" : "outline"} className={log.status === "Booked" ? "bg-[hsl(352_54%_33%)] text-white text-[10px]" : "text-[10px]"}>
                                    {log.status}
                                  </Badge>
                                </TableCell>
                                <TableCell className="text-right text-muted-foreground font-mono">{log.time}</TableCell>
                              </TableRow>)}
                          </TableBody>
                        </Table>
                      </div>
                    </div>}

                  {}
                  {activeTab === "campaigns" && <div className="space-y-4">
                      <div className="p-3 bg-muted/30 border border-border flex items-center justify-between text-xs font-mono">
                        <span>Schema Validation Health</span>
                        <span className="text-[hsl(352_54%_33%)] font-bold">100% Error Free</span>
                      </div>

                      <div className="border border-border overflow-x-auto">
                        <Table>
                          <TableHeader className="bg-muted/50">
                            <TableRow>
                              <TableHead className="text-xs font-mono">Optimization Module</TableHead>
                              <TableHead className="text-xs font-mono">Status</TableHead>
                              <TableHead className="text-xs font-mono">Health</TableHead>
                              <TableHead className="text-xs font-mono text-right">Impact</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {campaignData.map((item, i) => <TableRow key={item.id} data-index={i} className="text-xs">
                                <TableCell className="font-medium text-foreground">{item.campaign}</TableCell>
                                <TableCell>
                                  <span className="inline-flex items-center gap-1 text-[hsl(352_54%_33%)] font-mono text-[11px]">
                                    <CheckCircle2 className="size-3" />
                                    {item.status}
                                  </span>
                                </TableCell>
                                <TableCell className="font-mono">{item.health}</TableCell>
                                <TableCell className="text-right font-mono text-[hsl(352_54%_33%)] font-bold">{item.trend}</TableCell>
                              </TableRow>)}
                          </TableBody>
                        </Table>
                      </div>
                    </div>}

                  {}
                  <div className="pt-4 border-t border-border flex flex-col sm:flex-row items-center justify-between gap-4">
                    <div className="text-xs text-muted-foreground flex items-center gap-2">
                      <ShieldCheck className="size-4 text-[hsl(352_54%_33%)]" />
                      <span>Data backed by GoHighLevel integration & continuous AI tracking.</span>
                    </div>

                    <Link to="/VisibilityCheckupPage">
                      <Button variant="outline" size="sm" className="gap-2 text-xs">
                        View Full Diagnostic Report
                        <ArrowUpRight className="size-3.5" />
                      </Button>
                    </Link>
                  </div>
                </motion.div>
              </AnimatePresence>
            </div>
          </div>
        </Card>

        {}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 pt-4">
          {summaryCards.map((card, i) => <Card key={card.title} data-index={i} className="bg-card text-card-foreground border border-border hover:border-[hsl(352_54%_33%)] transition-colors duration-200 rounded-none p-6 space-y-3">
              <div className="size-10 rounded-none bg-muted/60 flex items-center justify-center border border-border">
                {renderSummaryIcon(card.iconName)}
              </div>
              <h3 className="text-lg font-bold font-default text-foreground">
                {card.title}
              </h3>
              <p className="text-sm text-muted-foreground leading-relaxed">
                {card.description}
              </p>
            </Card>)}
        </div>

        {}
        <div className="p-8 bg-muted/30 border border-border text-center space-y-6">
          <div className="space-y-2 max-w-2xl mx-auto">
            <h3 className="text-2xl font-bold font-default text-foreground">
              Ready to claim your spot in AI search recommendations?
            </h3>
            <p className="text-sm text-muted-foreground">
              Book a strategy call or start with our complimentary AI visibility audit.
            </p>
          </div>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link className="bg-[hsl(352_54%_33%)] hover:bg-[hsl(352_54%_33%)]/90 text-white font-semibold px-8 underline cursor-pointer" to="/PricingPlansPage">
              <Button size="lg" className="inline-flex items-center justify-center gap-2 whitespace-nowrap text-sm font-medium transition-all disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4 shrink-0 [&_svg]:shrink-0 outline-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive text-primary-foreground shadow-xs hover:bg-primary/90 h-10 rounded-md px-6 has-[>svg]:px-4 bg-destructive">Get started now</Button>
            </Link>

            <Link to="/VisibilityCheckupPage">
              <Button variant="outline" size="lg" className="border-border hover:bg-muted/60">
                Get a free AI visibility checkup
              </Button>
            </Link>
          </div>
        </div>
      </div>
    </section>;
}